import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { IThirdPartyEcrData, IThirdPartyEcrEquipmentTypes } from '@ruby/modules/third-party/models/third-party.interface';
import { IStation } from '@ruby/shared/models/commons/station.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-third-party-ecr',
  templateUrl: './third-party-ecr.dialog.html',
  styleUrls: ['./third-party-ecr.dialog.scss']
})
export class ThirdPartyEcrDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() equipmentTypes!: Array<IThirdPartyEcrEquipmentTypes>;
  @Input() states!: Array<INSAutoCompleteGroup>;
  @Input() destinationStations!: Array<INSAutoCompleteOption>;
  @Input() stccDescription!: Array<INSAutoCompleteOption>;
  @Input() railRoadCompanyData!: Array<INSAutoCompleteOption>;
  @Input() ecrEditData?: IThirdPartyEcrData;
  @Input() isLoadingDS!: boolean;
  @Input() isLoadingStcc!: boolean;
  @Input() isLoadingScac!: boolean;
  @Output() loadOriginStationsData = new EventEmitter<IStation>();
  @Output() loadScacData = new EventEmitter<string>();
  @Output() loadStccData = new EventEmitter<string>();
  @Output() setEcrData = new EventEmitter<IThirdPartyEcrData>();
  @Output() clearStcc = new EventEmitter();
  loading!: boolean;
  ecrForm: UntypedFormGroup = new UntypedFormGroup({
    equipmentTypes: new UntypedFormControl([]),
    railRoadCompany: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    originState: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    originCity: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    stccDescription: new UntypedFormControl('', [Validators.required, this.autocompleteValidator('stccDescription')])
  });
  debounceTimeout = 200;
  isEdit = false;
  private subscriptionNotifier = new Subject<void>();

  constructor(private utilsSvc: UtilsService) { }

  get chipsCtrl(): AbstractControl {
    if (!this.ecrForm.controls.equipmentTypes.value) {
      this.ecrForm.controls.equipmentTypes.setValue([]);
    }
    return this.ecrForm.controls.equipmentTypes;
  }

  get isFormValid(): boolean {
    return this.chipsCtrl.value.length > 0 &&
      this.ecrForm.controls.railRoadCompany.valid &&
      this.ecrForm.controls.originCity.valid &&
      this.ecrForm.controls.originState.valid &&
      this.ecrForm.controls.stccDescription.valid;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ecrEditData && changes.ecrEditData.currentValue) {
      this.ecrForm.patchValue(this.utilsSvc.deepCopy(changes.ecrEditData.currentValue));
      this.ecrForm.controls.originCity.enable();
    }

    if (changes.stccDescription && changes.stccDescription.currentValue.length === 0 && this.ecrEditData && !this.isEdit) {
      this.loadStccData.emit(this.ecrEditData.stccDescription.key);
      this.isEdit = true;
    }

    if (changes.stccDescription && changes.stccDescription.currentValue.length === 1 && this.isEdit) {
      this.ecrForm.controls.stccDescription.patchValue(changes.stccDescription.currentValue[0]);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next();
    this.subscriptionNotifier.complete();
  }

  ngOnInit(): void {
    this.ecrForm.controls.originState.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.ecrForm.controls.originCity.reset();
        this.ecrForm.controls.originCity.disable();
      }
    });

    this.ecrForm.controls.originCity.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((stationName: string) => {
        if (stationName && stationName.length > 2) {
          this.loadOriginStationsData.emit({
            stateAbbreviation: this.ecrForm.controls.originState.value.key,
            station: stationName
          });
        }
      });

    this.ecrForm.controls.railRoadCompany.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((companyName: string) => {
        if (companyName.length > 2) {
          this.loadScacData.emit(companyName);
        }
      });
    this.ecrForm.controls.stccDescription.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((stcc: string | INSAutoCompleteOption) => {
      if (typeof stcc === 'string') {
        if (stcc.length > 2) {
          this.loadStccData.emit(stcc.toUpperCase());
        } else {
          this.clearStcc.emit();
        }
      }
    });
  }

  /**
   * Select Chips
   *
   * @summary The following method add a selected chip to the list
   * @param $event: MatAutocompleteSelectedEvent
   * @returns void
   */
  selectChip($event: MatAutocompleteSelectedEvent): void {
    const code = $event.option.viewValue;
    if (!this.chipsCtrl.value.includes(code)) {
      this.chipsCtrl.value.push(code);
    }
  }

  /**
   * Remove Equipment
   *
   * @summary The following method removes an equipment from the list
   * @param equipment: string
   * @returns void
   */
  removeEquipment(equipment: string): void {
    const equipments = this.chipsCtrl.value.filter((item: string) => item !== equipment);
    this.chipsCtrl.setValue(equipments);
  }

  /**
   * autocomplete Selected
   *
   * @summary Method called when autocomplete is selected
   * @param formCtrlName: string
   * @param previousCtrlName: string
   * @returns void
   */
  autocompleteSelected(formCtrlName: string, previousCtrlName: string): void {
    if (typeof this.ecrForm.get(previousCtrlName)?.value !== 'string') {
      this.ecrForm.get(formCtrlName)?.enable();
    }
  }

  /**
   * Autocomplete Validator
   *
   * @summary Autocomplete Validator
   * @param controlName?: string
   * @returns ValidatorFn
   */
  autocompleteValidator(controlName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value !== '') {
        result = { invalidSelection: { value: control.value } };
      }

      if (controlName === 'stccDescription' && typeof control.value === 'string' && control.value.length > 7) {
        return { maxlength: { requiredLength: 7 } };
      }

      if (controlName === 'stccDescription' && typeof control.value === 'string' && control.value.length > 0) {
        return { custom: { message: 'No Results Found' } };
      }
      return result;
    };
  }

  /**
   * Emit Empty Car Request
   *
   * @summary This method emits the ECR data
   * @returns void
   */
  emitEcrData(): void {
    this.setEcrData.emit(this.ecrForm.value);
  }
}
